import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kExternalStorageBasePath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { MatterExternalSyncMappingResponse, MatterExternalSyncMappingResponseSchema } from '@/user_files/external-storage/schemas'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'

export async function getMatterExternalSyncMapping(matterId: string): Promise<MatterExternalSyncMappingResponse> {
  const auth = getAuth()
  const user = auth.currentUser
  if (!user || user.isAnonymous) throw Error(kErrorCodeAuthError)
  const sentryTransactionId = nanoid()

  try {
    const token = await user.getIdToken()
    if (auth.currentUser !== user) throw new DOMException('Account changed before matter sync request', 'AbortError')
    if (!token) throw Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kExternalStorageBasePath}/mappings/matter/${encodeURIComponent(matterId)}`
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
      },
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatUserFilesErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterId,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, detail || `Failed to fetch matter external sync mapping: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Matter external sync mapping response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterId,
            parseError: formatUnknownError(parseError),
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid matter external sync mapping response JSON')
    }

    const parsed = MatterExternalSyncMappingResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Matter external sync mapping response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })

      throw new ApiResponsePayloadError(response.status, 'Invalid matter external sync mapping response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, { extra: { matterId } })
      })
    }
    throw error
  }
}
